import { ArrowRight } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import { PrintButton, PrintSheetHeading } from "./PrintSheet";
import type { ViewMode } from "../lib/view-mode";

export type CollectionCell = {
  text: string;
  content?: ReactNode;
  expandable?: boolean;
};

export type CollectionRecord = {
  id: string;
  title: string;
  number?: string;
  typeLabel?: string;
  icon?: ReactNode;
  leading?: ReactNode;
  /** Riga sotto il titolo nelle viste a card: luogo o riferimento principale, con la sua icona. */
  subtitle?: ReactNode;
  /** Badge mostrato in alto a destra nelle viste a card, per le sezioni che hanno uno stato. */
  statusBadge?: ReactNode;
  /** Colonne da non ripetere nelle viste a card, perché già mostrate dal badge di stato. */
  hiddenInCards?: string[];
  /** Colonne da non ripetere nella sola vista Cubi, perché già mostrate come badge di tipo o sottotitolo. */
  hiddenInCubes?: string[];
  cells: Record<string, CollectionCell | null | undefined>;
  detailHref?: string;
  actions?: ReactNode;
};

export type CollectionColumn = {
  key: string;
  label: string;
};

/**
 * La stampa dell'elenco.
 *
 * Da ogni sezione si deve poter stampare quello che si sta guardando: i fornitori, gli ordini,
 * la squadra coi telefoni, le strutture coi referenti. Non esiste una stampa per sezione, esiste
 * questa: il pulsante vive qui, sulla vista Lista, e il foglio eredita ricerca, filtri e permessi
 * dalla pagina, senza chiedere niente di nuovo al server. Chi vede meno, stampa meno.
 *
 * `details` sono i filtri e la ricerca attivi, scritti a parole; `button` si spegne soltanto
 * quando la stessa schermata mostra piu elenchi e il pulsante deve restare uno solo.
 */
export type CollectionPrint = {
  section: string;
  details?: Array<string | null | undefined>;
  button?: boolean;
};

export function CollectionView({
  mode,
  ariaLabel,
  titleLabel,
  columns,
  records,
  print,
}: {
  mode: ViewMode;
  ariaLabel: string;
  titleLabel: string;
  columns: CollectionColumn[];
  records: CollectionRecord[];
  print?: CollectionPrint;
}) {
  if (mode === "list") {
    return (
      <div className="collection-view" data-view={mode}>
        {print && (
          <div className="collection-view__print">
            {print.button !== false && <PrintButton />}
            <PrintSheetHeading title={print.section} details={print.details} />
          </div>
        )}
        <div className="collection-table-wrap" tabIndex={0}>
          <table className="collection-table" aria-label={ariaLabel}>
            <thead>
              <tr>
                <th scope="col">{titleLabel}</th>
                {columns.map((column) => <th scope="col" key={column.key}>{column.label}</th>)}
                <th scope="col"><span className="sr-only">Azioni</span></th>
              </tr>
            </thead>
            <tbody>
              {records.map((record) => (
                <tr key={record.id}>
                  <th scope="row">
                    <span className="collection-table__cell-text" title={record.title} aria-label={record.title}>
                      {record.title}
                    </span>
                  </th>
                  {columns.map((column) => {
                    const cell = record.cells[column.key];
                    if (!cell) return <td key={column.key} />;
                    return (
                      <td key={column.key}>
                        {cell.expandable && !cell.content
                          ? (
                            <details className="collection-table__expandable">
                              <summary role="button" aria-label="Mostra testo completo">
                                <span className="collection-table__expandable-preview collection-table__cell-text" aria-hidden="true">{cell.text}</span>
                                <span className="collection-table__expandable-command" aria-hidden="true">Mostra tutto</span>
                              </summary>
                              <span className="collection-table__expandable-full">{cell.text}</span>
                            </details>
                          )
                          : cell.content ?? (
                            <span className="collection-table__cell-text" title={cell.text} aria-label={cell.text}>
                              {cell.text}
                            </span>
                          )}
                      </td>
                    );
                  })}
                  <td>
                    <div className="collection-item__actions">
                      {record.actions}
                      {record.detailHref && (
                        <Link aria-label={`Apri ${record.title}`} to={record.detailHref}>
                          <ArrowRight aria-hidden="true" />
                        </Link>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="collection-view" data-view={mode}>
      <div className="collection-items">
        {records.map((record) => (
          <article className="collection-item" key={record.id}>
            <header className="collection-item__top">
              <span>{record.number}</span>
              {/* Nei cubi il badge di stato, quando c'è, sostituisce l'etichetta di tipo invece di raddoppiarla. */}
              <span>{mode === "cubes" && record.statusBadge ? null : record.typeLabel}</span>
            </header>
            {record.statusBadge && <div className="collection-item__status">{record.statusBadge}</div>}
            {record.leading && <div className="collection-item__leading">{record.leading}</div>}
            {record.icon && <span className="collection-item__icon">{record.icon}</span>}
            <h3>{record.title}</h3>
            {/* Il sottotitolo col luogo vive solo nei cubi: nei rettangoli la stessa informazione è già una colonna. */}
            {mode === "cubes" && record.subtitle && <p className="collection-item__subtitle">{record.subtitle}</p>}
            <dl>
              {columns.flatMap((column) => {
                const cell = record.cells[column.key];
                const alreadyShown = record.hiddenInCards?.includes(column.key)
                  || (mode === "cubes" && record.hiddenInCubes?.includes(column.key));
                return !cell || cell.text === "" || alreadyShown
                  ? []
                  : [<div key={column.key}><dt>{column.label}</dt><dd>{cell.content ?? cell.text}</dd></div>];
              })}
            </dl>
            <footer className="collection-item__actions">
              {record.actions}
              {record.detailHref && (
                <Link aria-label={`Apri ${record.title}`} to={record.detailHref}>
                  <ArrowRight aria-hidden="true" />
                </Link>
              )}
            </footer>
          </article>
        ))}
      </div>
    </div>
  );
}
